/**
 * Service pour gérer les FAQ et leurs rich snippets
 */

export interface FAQItem {
  question: string;
  answer: string;
  helpUrl?: string;
}

export interface FAQData {
  items: FAQItem[];
  pageType: string;
}

/**
 * FAQ par défaut pour toutes les pages
 */
const DEFAULT_FAQ: FAQItem[] = [
  {
    question: "Qu'est-ce qu'Allocab ?",
    answer: "Allocab est le premier réseau de VTC en France. Nous proposons des services de transport privé avec chauffeur : VTC, taxi et taxi-moto dans toute la France."
  },
  {
    question: "Comment réserver un VTC avec Allocab ?",
    answer: "Vous pouvez réserver votre VTC en quelques clics sur notre site web ou notre application mobile. Choisissez votre trajet, sélectionnez votre véhicule et confirmez votre réservation."
  },
  {
    question: "Quels sont les avantages d'Allocab par rapport aux autres services ?",
    answer: "Allocab vous garantit des prix transparents, des chauffeurs professionnels, une couverture nationale et un service client disponible 7j/7."
  },
  {
    question: "Allocab est-il disponible dans ma ville ?",
    answer: "Allocab couvre plus de 1000 villes en France. Vérifiez la disponibilité dans votre ville directement sur notre site web."
  },
  {
    question: "Comment contacter le service client Allocab ?",
    answer: "Notre service client est disponible 7j/7 par téléphone, email ou via notre chat en ligne pour répondre à toutes vos questions."
  },
  {
    question: "Quels modes de paiement accepte Allocab ?",
    answer: "Nous acceptons les cartes bancaires, PayPal et les paiements en espèces selon les chauffeurs. Le paiement s'effectue de manière sécurisée."
  }
];

/**
 * FAQ spécifique aux aéroports parisiens
 */
const PARIS_AIRPORTS_FAQ: FAQItem = {
  question: "Quel est le prix d'un VTC depuis les aéroports parisiens ?",
  answer: "Les tarifs VTC depuis Orly et CDG vers Paris centre varient selon la destination et le type de véhicule. Utilisez notre simulateur pour obtenir un prix instantané et transparent."
};

/**
 * Vérifie si l'URL correspond à une page d'aéroports parisiens
 */
export function isParisAirportsPage(url: string): boolean {
  const parisAirportPatterns = [
    /\/vtc\/paris\/aeroport-(orly|cdg)/,
    /\/taxi\/paris\/aeroport-(orly|cdg)/
  ];
  
  return parisAirportPatterns.some(pattern => pattern.test(url));
}

/**
 * Détermine si les FAQ doivent être affichées sur cette page
 */
export function shouldDisplayFAQ(url: string): boolean {
  // Ne pas afficher sur les pages blog
  if (url.startsWith('/blog/')) {
    return false;
  }
  
  // Afficher sur toutes les autres pages
  return true;
}

/**
 * Récupère les données FAQ appropriées selon l'URL
 */
export function getFAQData(url: string): FAQData {
  let items = [...DEFAULT_FAQ];
  let pageType = 'default';
  
  if (isParisAirportsPage(url)) {
    items.push(PARIS_AIRPORTS_FAQ);
    pageType = 'paris-airports';
  }
  
  return {
    items,
    pageType
  };
}

/**
 * Génère les données structurées JSON-LD pour les FAQ
 */
export function generateFAQStructuredData(url: string): object {
  const faqData = getFAQData(url);
  
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqData.items.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer
      }
    }))
  };
}
